import {
  Smartphone,
  LayoutDashboard, Users, Shield, ShoppingCart, FileText,

  Settings, LifeBuoy, BookOpen, ScrollText, ArrowDownToLine,
  ArrowUpFromLine, ArrowLeftRight, LayoutGrid, Palette, Globe,
  Scale, BarChart3, Landmark, Coins, Gift, Clock, UserCog,
  BellRing, DatabaseZap, CreditCard, Wallet, DollarSign,
  TestTube, Copy, Megaphone, TrendingUp, Target, Award, Rocket,
  MessageSquare, Gem, ImageIcon, Layers, Flag, Star, Gavel, ShieldAlert,
  FlaskConical, Eye, SlidersHorizontal, UserCheck, Monitor,
  AlertTriangle, GraduationCap, ShieldCheck, ClipboardList,
  type LucideIcon,
  Send, Trophy,
} from "lucide-react";
import { FEE_SECTIONS } from "@/pages/admin/fees/feeSections";

export interface AdminSidebarItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: string;
  moduleKey?: string;
  children?: AdminSidebarItem[];
}

export interface AdminSidebarGroup {
  key: string;
  label: string;
  icon: LucideIcon;
  items: AdminSidebarItem[];
}

export const adminSidebarGroups: AdminSidebarGroup[] = [
  {
    key: "users",
    label: "Users & Access",
    icon: Users,
    items: [
      { to: "/admin/users", label: "Users", icon: Users, permission: "users.view" },
      { to: "/admin/verification-tiers", label: "Verification Tiers", icon: Shield, permission: "verification_tiers.view" },
      { to: "/admin/activation-queue", label: "Activation Queue", icon: Clock, permission: "users.view" },
      { to: "/admin/name-changes", label: "Name Change Requests", icon: FileText, permission: "kyc.view" },
      { to: "/admin/impersonation-logs", label: "Impersonation Logs", icon: Eye, permission: "users.impersonate" },
    ],
  },
  {
    key: "compliance",
    label: "KYC & Compliance",
    icon: Shield,
    items: [
      { to: "/admin/kyc", label: "KYC Queue", icon: Shield, permission: "kyc.view" },
      { to: "/admin/kyc-settings", label: "KYC Settings", icon: Settings, permission: "kyc.settings" },
      { to: "/admin/kyc-steps", label: "KYC Steps", icon: Settings, permission: "kyc.settings" },
      { to: "/admin/compliance-providers", label: "Compliance Providers", icon: Globe, permission: "kyc.settings" },
      { to: "/admin/aml-screenings", label: "AML Screening", icon: Shield, permission: "kyc.view" },
      { to: "/admin/compliance-watchlists", label: "Watchlists", icon: Flag, permission: "kyc.settings" },
      { to: "/admin/compliance-cases", label: "Compliance Cases", icon: Gavel, permission: "kyc.view" },
      { to: "/admin/edd-reviews", label: "EDD Reviews", icon: ShieldCheck, permission: "compliance.manage" },
      { to: "/admin/sar-filing", label: "SAR Filing", icon: AlertTriangle, permission: "compliance.manage" },
      { to: "/admin/compliance-reports", label: "Reports", icon: FileText, permission: "kyc.view" },
      { to: "/admin/compliance-logs", label: "Webhook Logs", icon: ScrollText, permission: "kyc.view" },
    ],
  },
  {
    key: "risk",
    label: "Risk & Security",
    icon: ShieldAlert,
    items: [
      { to: "/admin/risk-center", label: "Risk Control Center", icon: ShieldCheck, permission: "qa.view" },
      { to: "/admin/transaction-monitoring", label: "Transaction Monitoring", icon: TrendingUp, permission: "kyc.view" },
      { to: "/admin/transaction-risk", label: "Transaction Risk", icon: TrendingUp, permission: "kyc.view" },
      { to: "/admin/monitoring-rules", label: "Monitoring Rules", icon: Settings, permission: "kyc.settings" },
      { to: "/admin/risk-profiles", label: "Risk Profiles", icon: ShieldAlert, permission: "kyc.view" },
      { to: "/admin/security-center", label: "Security Center", icon: ShieldCheck, permission: "qa.view" },
      {
        to: "/admin/security-events", label: "Security", icon: Shield,
        children: [
          { to: "/admin/security-events", label: "Security Events", icon: Shield, permission: "audit_logs.view" },
          { to: "/admin/ip-allowlists", label: "IP Allowlists", icon: Globe, permission: "security.ip_allowlist" },
          { to: "/admin/session-monitor", label: "Session Monitor", icon: Monitor, permission: "security.sessions" },
          { to: "/admin/captcha-settings", label: "CAPTCHA", icon: ShieldCheck, permission: "settings.edit" },
        ],
      },
      { to: "/admin/financial-integrity", label: "Financial Integrity", icon: Scale, permission: "financial_integrity.view" },
    ],
  },
  {
    key: "operations",
    label: "Money & Operations",
    icon: Wallet,
    items: [
      {
        to: "/admin/wallets", label: "Wallets", icon: Wallet, permission: "wallets.view",
        children: [
          { to: "/admin/wallets", label: "All Wallets", icon: Wallet, permission: "wallets.view" },
          { to: "/admin/wallets?type=spot", label: "Spot", icon: Wallet, permission: "wallets.view" },
          { to: "/admin/wallets?type=funding", label: "Funding", icon: Wallet, permission: "wallets.view" },
          { to: "/admin/wallets?frozen=1", label: "Frozen", icon: ShieldAlert, permission: "wallets.view" },
          { to: "/admin/wallets/transfers", label: "Transfer Logs", icon: ArrowLeftRight, permission: "wallets.view" },
        ],
      },
      {
        to: "/admin/deposits", label: "Deposits", icon: ArrowDownToLine, permission: "crypto_deposits.view",
        children: [
          { to: "/admin/deposits", label: "All Deposits", icon: ArrowDownToLine, permission: "crypto_deposits.view" },
          { to: "/admin/deposits?status=pending", label: "Pending", icon: Clock, permission: "crypto_deposits.view" },
          { to: "/admin/deposits?status=initiated", label: "Initiated", icon: Clock, permission: "crypto_deposits.view" },
          { to: "/admin/deposits?status=success", label: "Successful", icon: ShieldCheck, permission: "crypto_deposits.view" },
          { to: "/admin/deposits?status=approved", label: "Approved", icon: ShieldCheck, permission: "crypto_deposits.view" },
          { to: "/admin/deposits?status=rejected", label: "Rejected", icon: ShieldAlert, permission: "crypto_deposits.view" },
        ],
      },
      {
        to: "/admin/withdrawals", label: "Withdrawals", icon: ArrowUpFromLine, permission: "crypto_withdrawals.view",
        children: [
          { to: "/admin/withdrawals", label: "All Withdrawals", icon: ArrowUpFromLine, permission: "crypto_withdrawals.view" },
          { to: "/admin/withdrawals?status=pending", label: "Pending", icon: Clock, permission: "crypto_withdrawals.view" },
          { to: "/admin/withdrawals?status=approved", label: "Approved", icon: ShieldCheck, permission: "crypto_withdrawals.view" },
          { to: "/admin/withdrawals?status=processing", label: "Processing", icon: Clock, permission: "crypto_withdrawals.view" },
          { to: "/admin/withdrawals?status=completed", label: "Completed", icon: ShieldCheck, permission: "crypto_withdrawals.view" },
          { to: "/admin/withdrawals?status=rejected", label: "Rejected", icon: ShieldAlert, permission: "crypto_withdrawals.view" },
        ],
      },
      { to: "/admin/payouts", label: "Payouts", icon: Send, permission: "crypto_withdrawals.view" },
      {
        to: "/admin/tx", label: "Transactions", icon: FileText, permission: "transactions.view",
        children: [
          { to: "/admin/tx", label: "All", icon: FileText, permission: "transactions.view" },
          { to: "/admin/tx?type=deposit", label: "Deposits", icon: ArrowDownToLine, permission: "transactions.view" },
          { to: "/admin/tx?type=withdraw", label: "Withdrawals", icon: ArrowUpFromLine, permission: "transactions.view" },
          { to: "/admin/wallets/transfers", label: "Transfers", icon: ArrowLeftRight, permission: "wallets.view" },
          { to: "/admin/tx?status=failed", label: "Failed", icon: ShieldAlert, permission: "transactions.view" },
        ],
      },
      { to: "/admin/transactions", label: "Transactions", icon: FileText, permission: "transactions.view" },
      { to: "/admin/orders", label: "Orders", icon: ShoppingCart, permission: "orders.view" },
      { to: "/admin/exchanges", label: "Exchanges", icon: ArrowLeftRight, permission: "exchanges.view" },
      { to: "/admin/fiat-deposits", label: "Fiat Deposits", icon: ArrowDownToLine, permission: "fiat_deposits.view", moduleKey: "wallets" },
      { to: "/admin/fiat-withdrawals", label: "Fiat Withdrawals", icon: ArrowUpFromLine, permission: "fiat_withdrawals.view", moduleKey: "wallets" },
      { to: "/admin/crypto-deposits", label: "Crypto Deposits", icon: ArrowDownToLine, permission: "crypto_deposits.view" },
      { to: "/admin/crypto-withdrawals", label: "Crypto Withdrawals", icon: ArrowUpFromLine, permission: "crypto_withdrawals.view" },
      { to: "/admin/deposit-reconciliation", label: "Deposit Reconciliation", icon: FileText, permission: "crypto_deposits.view" },
      { to: "/admin/withdrawal-activity", label: "Withdrawal Activity", icon: FileText, permission: "crypto_withdrawals.view" },
      { to: "/admin/exchange-wallets", label: "Exchange Wallets", icon: Wallet, permission: "crypto.view" },
    ],
  },
  {
    key: "payment_gateways",
    label: "Payment Gateways",
    icon: CreditCard,
    items: [
      { to: "/admin/integrations?tab=fiat", label: "Fiat Gateways", icon: CreditCard, permission: "settings.integrations" },
      { to: "/admin/integrations?tab=payments", label: "Crypto Payment Gateways", icon: Coins, permission: "settings.integrations" },
      { to: "/admin/fiat-settings", label: "Deposit & Withdraw Methods", icon: Settings, permission: "fiat_settings.view", moduleKey: "wallets" },
      { to: "/admin/fiat-currencies", label: "Fiat Currencies", icon: Coins, permission: "settings.edit", moduleKey: "wallets" },
    ],
  },
  {
    key: "blockchain",
    label: "Blockchain & Crypto",
    icon: Coins,
    items: [
      { to: "/admin/blockchain-providers", label: "Blockchain Providers", icon: Globe, permission: "crypto.settings" },
      { to: "/admin/integrations?tab=blockchain", label: "Coinsbuy Wallets", icon: Wallet, permission: "settings.integrations" },
      { to: "/admin/crypto-settings", label: "Crypto Assets & Networks", icon: Settings, permission: "crypto.settings" },
      { to: "/admin/crypto-addresses", label: "Crypto Addresses", icon: Wallet, permission: "crypto.view" },
      { to: "/admin/crypto-callback-logs", label: "Callback Logs", icon: ScrollText, permission: "crypto_deposits.view" },
    ],
  },
  {
    key: "fees",
    label: "Manage Platform Fees",
    icon: DollarSign,
    items: [
      ...FEE_SECTIONS.map((s) => ({ to: s.to, label: s.label, icon: s.icon, permission: s.permission, moduleKey: s.moduleKey })),
    ],
  },
  {
    key: "trading",
    label: "Trading",
    icon: BarChart3,
    items: [
      {
        to: "/admin/spot-markets", label: "Spot Trading", icon: BarChart3, moduleKey: "spot_trading",
        children: [
          { to: "/admin/spot-currencies", label: "Currencies", icon: Coins, permission: "spot.markets", moduleKey: "spot_trading" },
          { to: "/admin/spot-markets", label: "Markets", icon: BarChart3, permission: "spot.markets", moduleKey: "spot_trading" },
          { to: "/admin/spot-orders", label: "Orders", icon: ShoppingCart, permission: "spot.orders", moduleKey: "spot_trading" },
          { to: "/admin/spot-trades", label: "Trades", icon: FileText, permission: "spot.trades", moduleKey: "spot_trading" },
          { to: "/admin/spot-settings", label: "Settings", icon: Settings, permission: "spot.settings", moduleKey: "spot_trading" },
        ],
      },
      {
        to: "/admin/futures-contracts", label: "Futures", icon: TrendingUp, moduleKey: "futures",
        children: [
          { to: "/admin/futures-contracts", label: "Contracts", icon: TrendingUp, permission: "futures.contracts", moduleKey: "futures" },
          { to: "/admin/futures-orders", label: "Orders", icon: ShoppingCart, permission: "futures.orders", moduleKey: "futures" },
          { to: "/admin/futures-positions", label: "Positions", icon: FileText, permission: "futures.positions", moduleKey: "futures" },
          { to: "/admin/futures-settings", label: "Settings", icon: Settings, permission: "futures.settings", moduleKey: "futures" },
        ],
      },
      {
        to: "/admin/p2p", label: "P2P", icon: ArrowLeftRight, moduleKey: "p2p",
        children: [
          { to: "/admin/p2p", label: "Dashboard", icon: ArrowLeftRight, permission: "p2p.view", moduleKey: "p2p" },
          { to: "/admin/p2p-merchants", label: "Merchants", icon: Users, permission: "p2p.merchants", moduleKey: "p2p" },
          { to: "/admin/p2p-orders", label: "Orders", icon: ShoppingCart, permission: "p2p.orders", moduleKey: "p2p" },
          { to: "/admin/p2p-disputes", label: "Disputes", icon: Scale, permission: "p2p.disputes", moduleKey: "p2p" },
          { to: "/admin/p2p-settings", label: "Settings", icon: Settings, permission: "p2p.settings", moduleKey: "p2p" },
        ],
      },
      { to: "/admin/copy-trading", label: "Copy Trading", icon: Copy, permission: "copy_trading.view", moduleKey: "copy_trading" },
      {
        to: "/admin/binary-durations", label: "Binary Options", icon: Clock, moduleKey: "binary_trading",
        children: [
          { to: "/admin/binary-markets", label: "Markets", icon: BarChart3, permission: "binary_options.view", moduleKey: "binary_trading" },
          { to: "/admin/binary-durations", label: "Durations", icon: Clock, permission: "binary_options.view", moduleKey: "binary_trading" },
          { to: "/admin/binary-trades", label: "Trades", icon: FileText, permission: "binary_options.view", moduleKey: "binary_trading" },
          { to: "/admin/binary-settings", label: "Settings", icon: Settings, permission: "binary_options.view", moduleKey: "binary_trading" },
        ],
      },
      {
        to: "/admin/forex-pairs", label: "Forex", icon: TrendingUp,
        children: [
          { to: "/admin/forex-pairs", label: "Pairs", icon: TrendingUp, permission: "forex.view" },
          { to: "/admin/forex-orders", label: "Orders", icon: ShoppingCart, permission: "forex.view" },
          { to: "/admin/forex-settings", label: "Settings", icon: Settings, permission: "forex.view" },
        ],
      },
      { to: "/admin/liquidity", label: "Liquidity Management", icon: Landmark, permission: "settings.integrations" },
      { to: "/admin/exchange-providers", label: "Exchange Providers", icon: ArrowLeftRight, permission: "settings.integrations" },
      { to: "/admin/trading-reports", label: "Trading Reports", icon: BarChart3, permission: "binary_options.view" },
    ],
  },
  {
    key: "products",
    label: "Products",
    icon: Coins,
    items: [
      {
        to: "/admin/stakings", label: "Stakings", icon: Landmark, permission: "staking.view", moduleKey: "staking",
        children: [
          { to: "/admin/stakings", label: "All Positions", icon: Landmark, permission: "staking.view", moduleKey: "staking" },
          { to: "/admin/stakings?status=active", label: "Active", icon: Clock, permission: "staking.view", moduleKey: "staking" },
          { to: "/admin/stakings?status=matured", label: "Matured", icon: ShieldCheck, permission: "staking.view", moduleKey: "staking" },
          { to: "/admin/stakings?note_status=listed", label: "Listed (Notes)", icon: FileText, permission: "staking.view", moduleKey: "staking" },
          { to: "/admin/stakings?status=closed", label: "Closed", icon: ShieldAlert, permission: "staking.view", moduleKey: "staking" },
          { to: "/admin/staking-plans", label: "Plans", icon: Settings, permission: "staking.view", moduleKey: "staking" },
        ],
      },
      { to: "/admin/staking-plans", label: "Staking Panel", icon: Landmark, permission: "staking.view", moduleKey: "staking" },
      { to: "/admin/earn-projects", label: "Earn Projects", icon: Coins, permission: "earn.view", moduleKey: "earn" },
      { to: "/admin/legacy-notes", label: "Notes Market", icon: FileText, permission: "legacy_notes.view", moduleKey: "legacy_notes" },
      { to: "/admin/time-boxes", label: "Time Boxes", icon: Clock, permission: "time_boxes.view" },
      {
        to: "/admin/token-sale-offerings", label: "Launchpad", icon: Rocket, moduleKey: "token_sale",
        children: [
          { to: "/admin/token-sale-offerings", label: "Offerings", icon: Coins, permission: "token_sale.view", moduleKey: "token_sale" },
          { to: "/admin/token-sale-purchases", label: "Purchases", icon: ShoppingCart, permission: "token_sale.view", moduleKey: "token_sale" },
          { to: "/admin/launchpad-notify", label: "Notify List", icon: BellRing, permission: "token_sale.view", moduleKey: "token_sale" },
        ],
      },
      { to: "/admin/listing-applications", label: "Listing Applications", icon: ClipboardList, permission: "listings.view" },
      { to: "/admin/listing-control", label: "Listing Control", icon: Layers, permission: "kyc.view" },
      {
        to: "/admin/virtual-cards", label: "Virtual Cards", icon: CreditCard, moduleKey: "virtual_cards",
        children: [
          { to: "/admin/virtual-cards", label: "Cards", icon: CreditCard, permission: "virtual_cards.view", moduleKey: "virtual_cards" },
          { to: "/admin/virtual-card-settings", label: "Card Settings", icon: Settings, permission: "virtual_cards.settings", moduleKey: "virtual_cards" },
        ],
      },
      { to: "/admin/token-config", label: "Token Config", icon: Coins, permission: "settings.edit" },
      {
        to: "/admin/nft-dashboard", label: "NFT Marketplace", icon: Gem,
        children: [
          { to: "/admin/nft-dashboard", label: "Dashboard", icon: LayoutDashboard, permission: "nft.view" },
          { to: "/admin/nft-collections", label: "Collections", icon: Layers, permission: "nft.manage" },
          { to: "/admin/nft-items", label: "Items", icon: ImageIcon, permission: "nft.manage" },
          { to: "/admin/nft-creators", label: "Creators", icon: Users, permission: "nft.verify_creator" },
          { to: "/admin/nft-listings", label: "Listings", icon: ShoppingCart, permission: "nft.manage" },
          { to: "/admin/nft-sales", label: "Sales", icon: DollarSign, permission: "nft.view" },
          { to: "/admin/nft-reports", label: "Reports", icon: Flag, permission: "nft.moderate" },
          { to: "/admin/nft-categories", label: "Categories", icon: LayoutGrid, permission: "nft.settings" },
          { to: "/admin/nft-networks", label: "Networks", icon: Globe, permission: "nft.settings" },
          { to: "/admin/nft-settings", label: "Settings", icon: Settings, permission: "nft.settings" },
          { to: "/admin/nft-featured", label: "Featured", icon: Star, permission: "nft.featured" },
        ],
      },
      {
        to: "/admin/web3-chains", label: "Web3 Hub", icon: Globe, moduleKey: "web3_hub",
        children: [
          { to: "/admin/web3-chains", label: "Chains & Tokens", icon: Globe, permission: "web3.view", moduleKey: "web3_hub" },
          { to: "/admin/web3-quests", label: "Quests", icon: Target, permission: "web3.settings", moduleKey: "web3_hub" },
          { to: "/admin/web3-settings", label: "Settings", icon: Settings, permission: "web3.settings", moduleKey: "web3_hub" },
        ],
      },
    ],
  },
  {
    key: "growth",
    label: "Growth & Marketing",
    icon: Gift,
    items: [
      { to: "/admin/marketing", label: "Creator Program", icon: Megaphone, permission: "marketing.view", moduleKey: "marketing" },
      {
        to: "/admin/marketer-hq", label: "Marketer HQ", icon: Megaphone, moduleKey: "marketing",
        children: [
          { to: "/admin/marketer-hq", label: "Members", icon: Megaphone, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/marketer-hq?tab=tasks", label: "Content Tasks", icon: Target, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/marketer-hq?tab=catalogue", label: "Task Catalogue", icon: Target, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/marketer-hq?tab=rewards", label: "Rewards", icon: DollarSign, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/marketing?tab=training", label: "Training", icon: Star, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/marketer-hq?tab=analytics", label: "Analytics", icon: Trophy, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/marketer-hq?tab=settings", label: "Settings", icon: Settings, permission: "marketing.view", moduleKey: "marketing" },
        ],
      },
      {
        to: "/admin/ambassador-program", label: "Ambassador", icon: Trophy, moduleKey: "marketing",
        children: [
          { to: "/admin/ambassador-program", label: "Ambassadors", icon: Trophy, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/ambassador-program?tab=campaigns", label: "Campaigns", icon: Megaphone, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/ambassador-program?tab=communities", label: "Community", icon: Globe, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/ambassador-program?tab=events", label: "Events", icon: Star, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/ambassador-program?tab=reports", label: "Reports", icon: Target, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/ambassador-program?tab=rewards", label: "Rewards", icon: DollarSign, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/ambassador-program?tab=analytics", label: "Analytics", icon: Trophy, permission: "marketing.view", moduleKey: "marketing" },
          { to: "/admin/ambassador-program?tab=settings", label: "Settings", icon: Settings, permission: "marketing.view", moduleKey: "marketing" },
        ],
      },
      {
        to: "/admin/referral-settings", label: "Referrals", icon: Gift, moduleKey: "referrals",
        children: [
          { to: "/admin/referral-settings", label: "Overview", icon: Settings, permission: "referrals.settings", moduleKey: "referrals" },
          { to: "/admin/referral-commissions", label: "Commissions", icon: DollarSign, permission: "referrals.commissions", moduleKey: "referrals" },
          { to: "/admin/referral-leaderboard", label: "Leaderboard", icon: Trophy, permission: "referrals.commissions", moduleKey: "referrals" },
        ],
      },
    ],
  },
  {
    key: "support",
    label: "Support & Content",
    icon: LifeBuoy,
    items: [
      { to: "/admin/support", label: "Support", icon: LifeBuoy, permission: "support.view" },
      { to: "/admin/live-chat", label: "Live Chat", icon: MessageSquare, permission: "support.view", moduleKey: "support_tickets" },
      { to: "/admin/content", label: "Content", icon: BookOpen, permission: "content.view" },
      { to: "/admin/context-faqs", label: "Context FAQs", icon: BookOpen, permission: "content.view" },
      { to: "/admin/landing-page", label: "Landing Page", icon: Globe, permission: "settings.landing" },
      { to: "/admin/social-links", label: "Social Links", icon: Globe, permission: "settings.edit" },
      { to: "/admin/branding", label: "Branding", icon: Palette, permission: "settings.branding" },
    ],
  },
  {
    key: "notifications",
    label: "Notifications",
    icon: BellRing,
    items: [
      { to: "/admin/notification-events", label: "Notification Events", icon: BellRing, permission: "settings.edit" },
      { to: "/admin/notification-templates", label: "Notification Templates", icon: BellRing, permission: "settings.edit" },
    ],
  },
  {
    key: "admin",
    label: "Staff & Audit",
    icon: UserCog,
    items: [
      {
        to: "/admin/staff", label: "Staff", icon: UserCog,
        children: [
          { to: "/admin/staff", label: "Users", icon: UserCog, permission: "staff.view" },
          { to: "/admin/staff/roles", label: "Roles & Permissions", icon: Shield, permission: "roles.view" },
          { to: "/admin/staff/departments", label: "Departments", icon: LayoutGrid, permission: "departments.view" },
          { to: "/admin/scope-assignment", label: "Scope Restrictions", icon: SlidersHorizontal, permission: "staff.scopes" },
          { to: "/admin/temporary-access", label: "Temporary Access", icon: Clock, permission: "staff.delegate" },
          { to: "/admin/delegation", label: "Delegation", icon: UserCheck, permission: "staff.delegate" },
        ],
      },
      {
        to: "/admin/approval-queue", label: "Approvals", icon: ShieldAlert,
        children: [
          { to: "/admin/approval-queue", label: "Queue", icon: ShieldAlert, permission: "approvals.view" },
          { to: "/admin/approval-policies", label: "Policies", icon: Shield, permission: "approvals.manage" },
        ],
      },
      { to: "/admin/compliance-training", label: "Staff Training", icon: GraduationCap, permission: "compliance.manage" },
      { to: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText, permission: "audit_logs.view" },
      { to: "/admin/assignment-history", label: "Assignment History", icon: ScrollText, permission: "audit_logs.view" },
      { to: "/admin/privilege-logs", label: "Privilege Logs", icon: Eye, permission: "privilege_logs.view" },
    ],
  },
  {
    key: "system",
    label: "System",
    icon: Settings,
    items: [
      { to: "/admin/modules", label: "Modules", icon: LayoutGrid, permission: "settings.modules" },
      { to: "/admin/integrations", label: "Integrations", icon: Settings, permission: "settings.integrations" },
      { to: "/admin/mobile-api", label: "Mobile API Console", icon: Smartphone, permission: "settings.integrations" },
      { to: "/admin/system-qa", label: "System QA", icon: FlaskConical, permission: "qa.view" },
      { to: "/admin/demo-settings", label: "Demo Settings", icon: TestTube, permission: "settings.edit", moduleKey: "demo_trading" },
      { to: "/admin/data-migration", label: "Data Migration", icon: DatabaseZap, permission: "settings.edit" },
      { to: "/admin/migration-cleanup", label: "Migration Cleanup", icon: DatabaseZap, permission: "settings.edit" },
    ],
  },
];
